import {
  createGovernmentIdentifier,
  identifierIdentity,
  identifierName,
  updateBusinessEntity,
  updateGovernmentIdentifier,
  updateStockClass,
  useGovernmentIdentifiers,
  useStockClasses,
} from '@commenda/api';
import {
  Button,
  DatePicker,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  FieldWrapper,
  Input,
  InputErrorMessage,
  Label,
  LoadingIndicator,
} from '@commenda/kakashi';
import { ServiceRequestDetails } from '@commenda/types/src/service-request/types';
import { zodResolver } from '@hookform/resolvers/zod';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { KeyedMutator } from 'swr';
import { useNotification } from '../../../../hooks/useNotification';
import {
  IncorporationDetailsSchema,
  IncorporationDetailsSchemaFields,
} from '../schema/incorporation-details-schema';

type IncorporationDetailModalProps = {
  isOpen: boolean;
  setIsOpen: Dispatch<SetStateAction<boolean>>;
  onConfirm: (formData: IncorporationDetailsSchemaFields) => void;
  serviceRequest: ServiceRequestDetails;
  mutateServiceRequest: KeyedMutator<unknown>;
};

export const IncorporationDetailsModal = ({
  isOpen,
  setIsOpen,
  onConfirm,
  serviceRequest,
  mutateServiceRequest,
}: IncorporationDetailModalProps) => {
  const { setNotification } = useNotification();

  const {
    handleSubmit,
    register,
    formState: { errors, isSubmitting },
    setValue,
    watch,
    reset,
    clearErrors,
  } = useForm<IncorporationDetailsSchemaFields>({
    resolver: zodResolver(IncorporationDetailsSchema),
    defaultValues: {
      formationDate: null,
      corporationName: '',
      shareCount: null,
    },
  });

  const {
    data: stockClasses,
    error: stockClassesError,
    mutate: mutateStockClasses,
  } = useStockClasses(serviceRequest.corporationId);

  const stockClass = stockClasses?.[0];

  // step management
  const [step, setStep] = useState<1 | 2>(1);

  // ------------ Government Identifiers ------------
  const { data: giResp, mutate: mutateGovernmentIdentifiers } =
    useGovernmentIdentifiers(serviceRequest.corporationId);
  const governmentIdentifiers = giResp?.governmentIdentifiers;

  const [giValues, setGiValues] = useState<Record<string, string>>({});
  const [giErrors, setGiErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!governmentIdentifiers?.length) return;
    const initial: Record<string, string> = {};
    governmentIdentifiers.forEach((gi) => {
      initial[identifierIdentity(gi)] = gi.value ?? '';
    });
    setGiValues(initial);
  }, [governmentIdentifiers]);

  /**
   * Pre-populate form fields once the modal is open and we have the
   * required data from the service request / stock class hooks. This runs
   * whenever the modal is opened or when the remote data updates.
   */
  useEffect(() => {
    if (!isOpen) return; // only attempt when modal visible

    // Ensure we have the corporation data
    const { corporation } = serviceRequest;
    if (!corporation) return;

    // Pre-set formation date if available
    if (corporation.formationDate) {
      setValue('formationDate', corporation.formationDate);
    }

    // Pre-set corporation legal name
    if (corporation.companyLegalName) {
      setValue('corporationName', corporation.companyLegalName);
    }

    // Pre-populate share count.
    // Prefer the value from the first stock class additional information (if any),
    // falling back to the corporation.shareCount field.
    const initialShares =
      stockClass?.additionalInformation?.authorizedShares;
    const corpShareCount = (corporation as { shareCount?: number }).shareCount;

    if (typeof initialShares === 'number' && !Number.isNaN(initialShares)) {
      setValue('shareCount', initialShares);
    } else if (
      typeof corpShareCount === 'number' &&
      !Number.isNaN(corpShareCount)
    ) {
      setValue('shareCount', corpShareCount);
    }

    // Clear validation errors that might have been triggered before data arrived
    clearErrors();
  }, [isOpen, serviceRequest, stockClass, setValue, clearErrors]);

  const validateGovernmentIdentifiers = () => {
    const errors: Record<string, string> = {};

    governmentIdentifiers?.forEach((gi) => {
      const value = giValues[identifierIdentity(gi)]?.trim() || '';
      if (!value) {
        errors[identifierIdentity(gi)] = `${gi.displayName} is required`;
      }
    });

    setGiErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const onFinalSubmit = async (formData: IncorporationDetailsSchemaFields) => {
    // Validate government identifiers before proceeding
    if (!validateGovernmentIdentifiers()) {
      return;
    }

    // ---------- Update Corporation Basics ----------
    const corpRes = await updateBusinessEntity(serviceRequest.corporationId, {
      legalName: formData.corporationName,
      ...(formData.formationDate ? { formationDate: formData.formationDate } : {}),
    });

    if (!corpRes || !corpRes.ok) {
      setNotification({
        show: true,
        valid: false,
        title: 'Error',
        message: 'Failed to update corporation details.',
      });
      return;
    }

    const stockRes = await updateStockClass(stockClass.id, {
      additionalInformation: { authorizedShares: +formData.shareCount },
    });

    if (!stockRes || !stockRes.ok) {
      setNotification({
        show: true,
        valid: false,
        title: 'Error',
        message: 'Failed to update share information.',
      });
      return;
    }

    // ---------- Handle Government Identifier Values ----------
    try {
      const updatePromises = governmentIdentifiers?.map((gi) => {
        const newVal = giValues[identifierIdentity(gi)] ?? '';
        if (newVal === gi.value) return null; // no change

        if (gi.id) {
          return updateGovernmentIdentifier(
            serviceRequest.corporationId,
            gi.id,
            {
              value: newVal,
            }
          );
        }
        if (newVal) {
          return createGovernmentIdentifier(serviceRequest.corporationId, {
            ...identifierName(gi),
            value: newVal,
          });
        }
        return null;
      });
      const giResponses = await Promise.all(updatePromises.filter(Boolean));

      const giFailures = giResponses.filter(
        (res) => !res || (res instanceof Response && !res.ok)
      );

      if (giFailures.length > 0) {
        setNotification({
          show: true,
          valid: false,
          title: 'Error',
          message: 'Failed to update one or more government identifiers.',
        });
        return;
      }

      await mutateGovernmentIdentifiers();
    } catch (err) {
      console.error('Error updating government identifiers', err);
      setNotification({
        show: true,
        valid: false,
        title: 'Error',
        message: 'Failed to update government identifiers.',
      });
      return;
    }

    await mutateStockClasses();
    await mutateServiceRequest();

    onConfirm({ ...formData });
    reset();
    setStep(1);
    setIsOpen(false);
  };

  if (!stockClass || stockClassesError) {
    if (stockClassesError) console.error(stockClassesError);

    return (
      <Dialog
        open={isOpen}
        onOpenChange={(val) => {
          setIsOpen(val);
          reset();
        }}
      >
        <DialogContent>
          <LoadingIndicator />
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(val) => {
        setIsOpen(val);
        reset();
      }}
    >
      <DialogContent
        className="p-0 flex flex-col gap-0"
        preventCloseOnOutsideClick
      >
        {step === 1 ? (
          <>
            <DialogHeader>
              <DialogTitle>Corporation Details</DialogTitle>
            </DialogHeader>
            <form
              onSubmit={handleSubmit(
                () => setStep(2),
                (formError: unknown) => console.log(formError)
              )}
            >
              <div className="p-6 flex flex-col gap-5">
                <FieldWrapper>
                  <Label>Date of incorporation*</Label>
                  <DatePicker
                    date={watch('formationDate')}
                    setDate={(value) => {
                      setValue('formationDate', value);
                    }}
                    hasError={!!errors?.formationDate?.message}
                    {...register('formationDate')}
                    validateDate={(date) => {
                      if (new Date(date) > new Date()) {
                        setNotification({
                          show: true,
                          valid: false,
                          title: 'Error',
                          message:
                            'Date of incorporation cannot be in the future.',
                        });
                        return false;
                      }
                      return true;
                    }}
                    disabled={[{ after: new Date() }]}
                  />
                  {errors.formationDate?.message ? (
                    <InputErrorMessage message={errors.formationDate.message} />
                  ) : null}
                </FieldWrapper>
                <FieldWrapper>
                  <Label>Entity's final name or number*</Label>
                  <Input
                    id="corporationName"
                    {...register('corporationName')}
                    error={errors.corporationName?.message}
                    defaultValue={watch('corporationName') || ''}
                    placeholder="Ex: Commenda Corporation"
                  />
                </FieldWrapper>
                <FieldWrapper>
                  <Label>Total no. of shares*</Label>
                  <Input
                    id="shareCount"
                    {...register('shareCount', {
                      setValueAs: (value) => {
                        const num = Number(value);
                        return isNaN(num) || value === '' ? 0 : num;
                      },
                    })}
                    error={errors.shareCount?.message}
                    defaultValue={watch('shareCount') || ''}
                    placeholder="Total shares issued"
                    type="number"
                  />
                </FieldWrapper>
              </div>
              <DialogFooter>
                <Button
                  variant="secondary"
                  type="button"
                  onClick={() => {
                    setIsOpen(false);
                    reset();
                    setStep(1);
                  }}
                >
                  Cancel
                </Button>
                <Button type="submit">Next</Button>
              </DialogFooter>
            </form>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle>Government Identifiers</DialogTitle>
            </DialogHeader>
            <form
              onSubmit={handleSubmit(onFinalSubmit, (formError: unknown) =>
                console.log(formError)
              )}
            >
              <div className="p-6 flex flex-col gap-5 max-h-[60vh] overflow-y-auto">
                {!governmentIdentifiers ? (
                  <LoadingIndicator />
                ) : governmentIdentifiers.length === 0 ? (
                  <p>
                    We don't have information about which government identifiers
                    are supported for this jurisdiction.
                    <br />
                    <br />
                    If you need to provide information about this corporation's
                    government identifiers, (ex: Corporation Entity Number),
                    talk to a member of the Engineering team and ask them to add
                    this jurisdiction to Commenda OS.
                    <br />
                    <br />
                    Otherwise, just hit 'save details' to save the information
                    from the previous step.
                  </p>
                ) : (
                  governmentIdentifiers.map((gi) => (
                    <FieldWrapper key={identifierIdentity(gi)}>
                      <Label>{gi.displayName}</Label>
                      <Input
                        id={`gi-${identifierIdentity(gi)}`}
                        value={giValues[identifierIdentity(gi)] ?? ''}
                        onChange={(e) => {
                          const { value } = e.target;
                          setGiValues((prev) => ({
                            ...prev,
                            [identifierIdentity(gi)]: value,
                          }));
                          // Clear error when user starts typing
                          if (giErrors[identifierIdentity(gi)]) {
                            setGiErrors((prev) => ({
                              ...prev,
                              [identifierIdentity(gi)]: '',
                            }));
                          }
                        }}
                        placeholder={gi.description || gi.displayName}
                        error={giErrors[identifierIdentity(gi)]}
                      />
                    </FieldWrapper>
                  ))
                )}
              </div>
              <DialogFooter>
                <Button
                  variant="secondary"
                  type="button"
                  onClick={() => setStep(1)}
                >
                  Back
                </Button>
                <Button type="submit" disabled={isSubmitting}>
                  {isSubmitting ? 'Saving...' : 'Save Details'}
                </Button>
              </DialogFooter>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
